import { useAuth } from "../AuthProvider";
import { MainMenu } from "../lib/navData";
import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import "./sidenav.css";
import '../pages/edit.css';
import text from '../lib/dictionary.json';
import useWindowDimensions from '../hooks/useWindowDimensions';
import { Box, Tooltip } from '@mui/material';
import { LogoutIcon, SidebarLogo, SidenavButton, WebsiteIcon } from "../Icons";
import ProjectInfo from '../pages/ProjectInfo';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SideNavCard from "../components/reusable/SideNavCard";
import { useAuthAccess } from "../hooks/useAuthAccess";

const MainNavLink = ({
    to,
    linkText,
    icon,
    onClick,
    show,
    open,
    placement,
    style = {},
    textStyle = {},
    subLink = false,
    haveChildren = false
}) => (
    <Tooltip
        title={open ? '' : linkText}
        arrow
        sx={{ backgroundColor: 'rgb(55, 65, 81)' }}
        placement={placement}>
        {to
            ? <NavLink
                className={'sidenav-settings-option' + (subLink ? ' sub-link' : '')}
                style={style}
                to={to}
                onClick={onClick ?? null}>
                <div className="sidenav-link-content">
                    {icon ?? null}
                    {open ? <span style={textStyle}>{linkText ?? ''}</span> : null}
                </div>
                {haveChildren && open
                    ? <span style={{ transform: `rotate(${show ? 180 : 0}deg)`, transition: 'all .5s ease' }}>
                        <KeyboardArrowDownIcon />
                    </span>
                    : null}
            </NavLink>
            : <div className={'sidenav-settings-option' + (subLink ? ' sub-link' : '')}
                style={style} onClick={onClick ?? null}>
                <div className="sidenav-link-content">
                    {icon ?? null}
                    {open ? <span style={textStyle}>{linkText ?? ''}</span> : null}
                </div>
                {haveChildren && open
                    ? <span style={{ transform: `rotate(${show ? 180 : 0}deg)`, transition: 'all .5s ease' }}>
                        <KeyboardArrowDownIcon />
                    </span>
                    : null}
            </div>}
    </Tooltip>
);

const Border = () => (
    <div className="sidenav-link-border" style={{
        width: 1,
        height: '100%',
        position: 'absolute',
        top: 0
    }}></div>
);

const SubMenu = ({ item, open, show, expand }) => {
    const [openCard, setOpenCard] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const { user: { type }, lang } = useAuth();
    const navigate = useNavigate();

    const { canAccess: isMainItemAllowed } = useAuthAccess({
        serviceKey: item.requiredService,
        permissionKey: item.permission,
        action: 'read'
    });

    if (!isMainItemAllowed) {
        return null;
    }

    const isRoleAllowed = (subitem) => {
        if (subitem.title === "mytasks" && type !== "Driver") return false;
        if (
            ['myhotelbookings', 'myticketbookings', 'supplier_dashboard'].includes(subitem.title) &&
            type !== 'Supplier'
        ) {
            return false;
        }
        return true;
    };

    return (
        <>
            <MainNavLink
                style={{ marginBottom: 4 }}
                to={item.path}
                open={open}
                placement={lang === 'en' ? 'right' : 'left'}
                haveChildren={item.subNav?.length > 0}
                show={show}
                icon={item.icon}
                linkText={text[lang][item.title]}
                onClick={item.subNav ? (e) => {
                    e.stopPropagation();
                    expand();
                    if (!open) {
                        setAnchorEl(e.currentTarget);
                        setOpenCard(true);
                    }
                } : null}
            />
            {show ? (
                open ? (
                    item.subNav?.map((subitem, index) => (
                        <SubNavItem
                            key={index}
                            subitem={subitem}
                            open={open}
                            lang={lang}
                            isRoleAllowed={isRoleAllowed(subitem)}
                        />
                    ))
                ) : (
                    <FilteredSideNavCard
                        subNav={item.subNav}
                        isRoleAllowed={isRoleAllowed}
                        anchorEl={anchorEl}
                        openCard={openCard}
                        setOpenCard={setOpenCard}
                        setAnchorEl={setAnchorEl}
                        navigate={navigate}
                    />
                )
            ) : null}
        </>
    );
};

const FilteredSideNavCard = ({ subNav, isRoleAllowed, anchorEl, openCard, setOpenCard, setAnchorEl, navigate }) => {
    const rawSubNav = Array.isArray(subNav) ? subNav : [];

    const filteredItems = rawSubNav.filter(subitem => {
        return isRoleAllowed(subitem);
    });

    return (
        <SideNavCard
            items={filteredItems.filter(subitem => {
                return true;
            })}
            anchorEl={anchorEl}
            open={openCard}
            onClose={() => {
                setOpenCard(false);
                setAnchorEl(null);
            }}
            onItemClick={(item) => navigate(item.path)}
        />
    );
};

const SubNavItem = ({ subitem, open, lang, isRoleAllowed }) => {
    const { canAccess } = useAuthAccess({
        serviceKey: subitem.requiredService,
        permissionKey: subitem.permission,
        action: 'read'
    });

    if (!canAccess || !isRoleAllowed) return null;

    return (
        <MainNavLink
            subLink
            open={open}
            placement={lang === 'en' ? 'right' : 'left'}
            style={{
                [`padding${lang === 'en' ? 'Left' : 'Right'}`]: 16
            }}
            linkText={text[lang][subitem.title]}
            icon={<Border />}
            textStyle={{ [`margin${lang === 'en' ? 'Left' : 'Right'}`]: 16 }}
            to={subitem.path}
        />
    );
};

export default function Sidenav() {
    const [open, setopen] = useState(true);
    const [openInfo, setOpenInfo] = useState(false);
    const [showMenu, setShowMenu] = useState([]);
    const { lang, setModel, logOut, setConfirmState } = useAuth();
    const { height, width } = useWindowDimensions(); // Extracted 'width' to monitor screen resize

    useEffect(() => {
        setShowMenu(prev => prev.length < MainMenu.length ? MainMenu.map(() => false) : prev);
    }, []);

    // 1. Auto-close side nav on small screens (e.g., width < 768px)
    useEffect(() => {
        if (width && width < 768) {
            setopen(false);
        } else {
            setopen(true);
        }
    }, [width]);

    const toggleOpen = () => {
        setopen(!open);
    }
    console.log("MainMenu", MainMenu)
    return (
        <React.Fragment>
            <Box className={'sidenav'}
                sx={{
                    width: open ? 'var(--sidebar-width)' : '4.25rem',
                    pt: '1.875rem',
                    pb: '1.875rem',
                    flexShrink: 0
                }}>
                <div>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        width: '100%',
                        marginBottom: '1.875rem',
                    }}>
                        <SidebarLogo />
                    </div>

                    <div className='separator' style={{
                        height: 1,
                        marginBottom: '0.5rem',
                        '--app-gradient-1': 'rgba(224, 225, 226, .5)',
                        '--app-separator': 'rgba(224, 225, 226, .8)',
                    }} />
                </div>
                <div className='links-list' style={{
                    height: height - 132, maxHeight: height - 132,
                    pr: '1.75rem',
                    pl: '1.75rem'
                }}>
                    {MainMenu.map((item, index) => (
                        <SubMenu
                            key={index}
                            item={item}
                            index={index}
                            open={open}
                            show={showMenu[index]}
                            expand={() => {
                                setModel(item.title);
                                // 2. Toggle submenu layout: If clicked item is already open, close it. Otherwise open it.
                                setShowMenu(prev => prev.map((currentValue, i) => i === index ? !currentValue : false));
                            }} />
                    ))}
                </div>

                <div className={'sidenav-settings' + (open ? '' : ' closed')}>
                    <div className='separator' style={{
                        height: 1,
                        marginBottom: 12,
                        '--app-gradient-1': 'rgba(224, 225, 226, .5)',
                        '--app-separator': 'rgba(224, 225, 226, .8)',
                    }}></div>
                    <MainNavLink
                        open={open}
                        placement={lang === 'en' ? 'right' : 'left'}
                        icon={<WebsiteIcon className='sidenav-icon' />}
                        linkText={text[lang]['info']}
                        onClick={() => setOpenInfo(true)} />
                    <MainNavLink
                        open={open}
                        placement={lang === 'en' ? 'right' : 'left'}
                        icon={<LogoutIcon className='sidenav-icon' />}
                        linkText={text[lang]['logout']}
                        onClick={() => setConfirmState({
                            open: true,
                            type: 'info',
                            message: text[lang]['logout-confirm'],
                            onConfirm: logOut
                        })} />
                    <button className='sidenav-menu-btn' onClick={toggleOpen} style={{
                        [lang === 'en' ? 'right' : 'left']: 0,
                        transform: `translate(${lang === 'en' ? '' : '-'}80%, -10%)`
                    }}>
                        <SidenavButton style={{
                            width: 32,
                            height: 32,
                            transform: `rotate(${lang === 'en' ? open ? 0 : 180 : open ? 180 : 0}deg)`
                        }} className='sidenav-icon' />
                    </button>
                </div>
            </Box>
            {openInfo && <ProjectInfo close={() => setOpenInfo(false)} />}
        </React.Fragment>
    )
}